import { InspectionTemplate } from '@sw-sw/lib-inspection-templates';
import { uniq } from 'lodash';
import moment from 'moment';
import xhrService from '../xhrService';
import db from '../../db/db';

const baseUrl = `/api/divisions`;

interface Division {
  id: number;
  name: string;
  createdAt?: Date;
}

export interface DivisionUser {
  userId: number;
  divisionId: number;
  user: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
  };
}

export type IndexApi<D = any[]> = (
  divisionId: number,
  pageNumber?: number,
  searchQuery?: string,
  resourceIds?: number[],
  paginated?: boolean,
) => Promise<D>;
export type DeleteApi = (id: number, data: number[]) => Promise<void>;

export type CreateApi<A = any, B = any> = (id: number, data: A) => Promise<B>;

export type ArchiveApi = (divisionId: number, id: number) => Promise<any>;

type DivisionApi = {
  index: (search?: string) => Promise<Division[]>;
  create: (data: any) => Promise<Division>;
  get: (id: number) => Promise<Division>;
  destroy: (id: number) => Promise<any>;
  update: (id: number, data: any) => Promise<Division>;
  users: {
    // add search and pagination that can be found in userApi#index
    index: IndexApi;
    create: CreateApi<{ users: { id: number }[] }>;
    delete: DeleteApi;
  };
  clients: {
    index: IndexApi;
    create: CreateApi<{ clients: { id: number }[] }>;
    delete: DeleteApi;
    getClientsForMultipleDivisions: (divisionIds: number[]) => Promise<any>;
  };
  regulations: {
    index: IndexApi;
    create: CreateApi<{ regulations: { id: number }[] }>;
    delete: DeleteApi;
  };
  inspectionTemplates: {
    index: IndexApi<InspectionTemplate[]>;
  };
  projects: {
    index: (
      divisionId?: number,
      pageNumber?: number,
      searchQuery?: string,
      projectTypes?: any,
      primaryContacts?: any,
    ) => Promise<any>;
    archive: {
      index: IndexApi;
      get: ArchiveApi;
      archive: ArchiveApi;
      restore: ArchiveApi;
    };
  };
  inspectionIntervals: {
    index: IndexApi;
    fetchWithDefaultTrue: IndexApi;
    create: CreateApi<{ inspectionIntervals: { id: number }[] }>;
    delete: DeleteApi;
  };
  reports: {
    index: IndexApi;
  };
  areas: {
    getAll: (divisionId: number) => Promise<any>;
    getPaginatedByDivId: IndexApi;
    index: () => Promise<any>;
    createNew: CreateApi<{ name: string }, any>;
    create: CreateApi<{ areas: { id: number }[] }, any>;
    destroy: DeleteApi;
    delete: DeleteApi;
    get: (id: number) => Promise<any>;
    update: (id: number, data: any) => Promise<any>;
    getAreasForMultipleDivisions: (divisionIds: number[]) => Promise<any>;
    clientProjects: {
      index: (clientId: number, areaId: number) => Promise<any>;
    };
    projects: {
      index: (
        divisionId: number,
        areaId: number,
        pageNumber?: number,
        searchQuery?: string,
      ) => Promise<any>;
      create: (
        areaId: number,
        projectIds: number[],
        force?: boolean,
      ) => Promise<any>;
      remove: (areaId: number, projectId: number) => Promise<any>;
    };
  };
  documentTypeGroups: {
    index: IndexApi;
    getAvailable: (divisionId:number) => Promise<any>;
    create: CreateApi<{ documentGroups: { id: number }[] }>;
    delete: DeleteApi;
  };
};

const divisionApi: DivisionApi = {
  index: (search?: string) => {
    const params: { s?: string } = {};

    if (search) {
      params.s = search;
    }

    return xhrService.get(baseUrl, { params }).then((res) => {
      return res.data.map((division: Division) => {
        return {
          ...division,
          createdAt: moment(division.createdAt).format('MM-DD-YYYY'),
        };
      });
    });
  },
  create: (data) => xhrService.post(`${baseUrl}`, data).then((res) => res.data),
  get: (id) =>
    xhrService
      .get(`${baseUrl}/${id}`)
      .then((res) => res.data)
      .catch(({ response }) => {
        throw new Error(response.data.message);
      }),
  destroy: (id) =>
    xhrService
      .delete(`${baseUrl}/${id}`)
      .then(() => true)
      .catch(({ response }) => {
        throw new Error(response.data.message);
      }),
  update: (id, data) =>
    xhrService.put(`${baseUrl}/${id}`, data).then((res) => res.data),
  users: {
    index: (id, pageNumber?: number, searchQuery?: string) => {
      const params: { s?: string; p?: number } = {};

      if (searchQuery) {
        params.s = searchQuery;
      }

      if (pageNumber) {
        params.p = pageNumber;
      }

      return xhrService
        .get(`${baseUrl}/${id}/users`, { params })
        .then(handlePaginatedResponse.bind(null, 'user'));
    },
    create: (id, data) => {
      const userIds = data.users.map((user: { id: number }) => user.id);

      return xhrService
        .post(`${baseUrl}/${id}/users`, { userIds: userIds })
        .then((res) => res.data);
    },
    delete: (id, data) => {
      return xhrService
        .delete(`${baseUrl}/${id}/users`, { data: { userIds: data } })
        .then((res) => res.data);
    },
  },
  clients: {
    index: (
      id,
      pageNumber?: number,
      searchQuery?: string,
      resourceIds?: number[],
      paginated?: boolean,
    ) => {
      const params: {
        s?: string;
        p?: number;
        clientIds?: number[];
        paginated?: boolean;
      } = {};

      if (searchQuery) {
        params.s = searchQuery;
      }

      if (pageNumber) {
        params.p = pageNumber;
      }

      if (resourceIds) {
        params.clientIds = uniq(resourceIds);
      }

      if (paginated) {
        params.paginated = paginated;
      }

      return xhrService
        .get(`${baseUrl}/${id}/clients`, { params })
        .then(handlePaginatedResponse.bind(null, 'client'));
    },
    create: (id, data) => {
      const clientIds = data.clients.map((client: { id: number }) => client.id);

      return xhrService
        .post(`${baseUrl}/${id}/clients`, { clientIds: clientIds })
        .then((res) => res.data);
    },
    delete: (id, data) => {
      return xhrService
        .delete(`${baseUrl}/${id}/clients`, { data: { clientIds: data } })
        .then((res) => res.data);
    },
    getClientsForMultipleDivisions: (divisionIds: number[]) => {
      return xhrService
        .get(`${baseUrl}clients/clients`, { params: { divisionIds } })
        .then((res) => res.data);
    },
  },
  regulations: {
    index: (id, pageNumber?: number, searchQuery?: string) => {
      const params: { s?: string; p?: number } = {};

      if (searchQuery) {
        params.s = searchQuery;
      }

      if (pageNumber) {
        params.p = pageNumber;
      }

      return xhrService
        .get(`${baseUrl}/${id}/regulations`, { params })
        .then(handlePaginatedResponse.bind(null, 'documentGroup'));
    },
    create: (id, data) => {
      const regulationIds = data.regulations.map(
        (reg: { id: number }) => reg.id,
      );

      return xhrService
        .post(`${baseUrl}/${id}/regulations`, { regulationIds: regulationIds })
        .then((res) => res.data);
    },
    delete: (id, data) => {
      return xhrService
        .delete(`${baseUrl}/${id}/regulations`, {
          data: { regulationIds: data },
        })
        .then((res) => res.data);
    },
  },
  inspectionTemplates: {
    index: (id) => {
      return xhrService
        .get(`${baseUrl}/${id}/inspectionTemplates`)
        .then((res) => res.data.map((dit: { inspectionTemplate: any }) => dit));
    },
  },
  projects: {
    index: (
      id?: number,
      pageNumber?: number,
      searchQuery?: string,
      projectTypes?: any,
      primaryContacts?: any,
    ) => {
      const params: {
        s?: string;
        p?: number;
        projectTypes?: number[];
        primaryContacts?: number[];
      } = {};

      if (searchQuery) {
        params.s = searchQuery;
      }

      if (pageNumber) {
        params.p = pageNumber;
      }

      if (projectTypes) {
        params.projectTypes = projectTypes.map(
          (pt: { value: number }) => pt.value,
        );
      }

      if (primaryContacts) {
        params.primaryContacts = primaryContacts.map(
          (pc: { value: number }) => pc.value,
        );
      }

      const isOnline = navigator.onLine;

      if (!isOnline) {
        return db.projects.toArray();
      } else if (id)
        return xhrService
          .get(`${baseUrl}/${id}/projects`, { params })
          .then(handlePaginatedResponse.bind(null, 'project'));
      else return Promise.resolve([]);
    },
    archive: {
      index: (divisionId, pageNumber?: number, searchQuery?: string) => {
        const params: {
          s?: string;
          p?: number;
          projectTypes?: number[];
          primaryContacts?: number[];
        } = {};

        if (searchQuery) {
          params.s = searchQuery;
        }

        if (pageNumber) {
          params.p = pageNumber;
        }

        return xhrService
          .get(`${baseUrl}/${divisionId}/projects/archive`, { params })
          .then(handlePaginatedResponse.bind(null, 'project'));
      },
      get: (divisionId: number, id: number) => {
        return xhrService
          .get(`${baseUrl}/${divisionId}/projects/archive/${id}`)
          .then((res: { data: any }) => res.data);
      },
      archive: (divisionId: number, id: number, notes?: string) => {
        return xhrService
          .post(`/api/divisions/${divisionId}/projects/archive/${id}`, { notes })
          .then((res) => res.data);
      },
      restore: (divisionId: number, id: number) => {
        return xhrService
          .delete(`/api/divisions/${divisionId}/projects/archive/${id}`)
          .then((res) => res.data);
      },
    },
  },
  inspectionIntervals: {
    index: (id?: number, pageNumber?: number, searchQuery?: string) => {
      const params: { s?: string; p?: number } = {};

      if (searchQuery) {
        params.s = searchQuery;
      }

      if (pageNumber) {
        params.p = pageNumber;
      }

      return xhrService
        .get(`${baseUrl}/${id}/inspection-intervals`, { params })
        .then((res) => {
          res.data.pageData = res.data.pageData.filter(
            (ele: any) => ele.inspectionInterval.default === false,
          );

          return res;
        })
        .then(handlePaginatedResponse.bind(null, 'inspectionInterval'));
    },
    fetchWithDefaultTrue: (
      id?: number,
      pageNumber?: number,
      searchQuery?: string,
    ) => {
      const params: { s?: string; p?: number } = {};

      if (searchQuery) {
        params.s = searchQuery;
      }

      if (pageNumber) {
        params.p = pageNumber;
      }

      return xhrService
        .get(`${baseUrl}/${id}/inspection-intervals`, { params })
        .then(handlePaginatedResponse.bind(null, 'inspectionInterval'));
    },
    create: (id, data) => {
      const inspectionIntervalIds = data.inspectionIntervals.map(
        (inspectionInterval: { id: number }) => inspectionInterval.id,
      );

      return xhrService
        .post(`${baseUrl}/${id}/inspection-intervals`, {
          inspectionIntervalIds: inspectionIntervalIds,
        })
        .then((res) => res.data);
    },
    delete: (id, inspectionIntervalIds) => {
      return xhrService
        .delete(`${baseUrl}/${id}/inspection-intervals`, {
          data: { inspectionIntervalIds: inspectionIntervalIds },
        })
        .then((res) => res.data);
    },
  },
  reports: {
    index: (id: number, pageNumber?: number, searchQuery?: string) => {
      const params: { s?: string; p?: number } = {};

      if (searchQuery) {
        params.s = searchQuery;
      }

      if (pageNumber) {
        params.p = pageNumber;
      }

      return xhrService
        .get(`${baseUrl}/${id}/reports`, { params })
        .then((res) => res.data);
    },
  },
  areas: {
    getAll: (divisionId: number) => {
      return xhrService
        .get(`/api/areas`, { params: { division_id: divisionId } })
        .then((res) => res.data);
    },
    getPaginatedByDivId: (
      id?: number,
      pageNumber?: number,
      searchQuery?: string,
    ) => {
      const params: { s?: string; p?: number } = {};
      
      if (searchQuery) {
        params.s = searchQuery;
      }
      if (pageNumber) {
        params.p = pageNumber;
      }

      return xhrService
        .get(`${baseUrl}/${id}/areas`, { params })
        .then(handlePaginatedResponse.bind(null, 'area'));
    },
    index: () => {
      return xhrService.get(`/api/areas/tenant`).then((res) => res.data);
    },
    createNew: (id, data) => {
      return xhrService
        .post(`/api/areas`, { name: data.name, tenantId: id })
        .then((res) => res.data);
    },
    create: (id, data) => {
      const areaIds = data.areas.map((area: { id: number }) => area.id);
      return xhrService
        .post(`/api/areas/add-area-division`, {
          areaIds: areaIds,
          divisionId: id,
        })
        .then((res) => res.data);
    },
    destroy: (_id, data) => {
      return xhrService
        .delete(`/api/areas/`, { data: { areaIds: data } })
        .then((res) => res.data);
    },
    delete: (id, data) => {
      return xhrService
        .delete(`/api/areas/remove/${id}`, { data: { areaIds: data } })
        .then((res) => res.data);
    },
    get: (id: number) => {
      return xhrService.get(`/api/areas/${id}`).then((res) => res.data);
    },
    update: (id: number, data: any) => {
      return xhrService
        .put(`/api/areas/${id}`, { name: data.name })
        .then((res) => res.data);
    },
    getAreasForMultipleDivisions: (divisionIds: number[]) => {
      return xhrService
        .get(`/api/areas/multiple-divisions-areas`, {
          params: { division_ids: divisionIds },
        })
        .then((res) => res.data);
    },
    clientProjects: {
      index: (clientId: number, areaId: number) => {
        return xhrService
          .get(`/api/areas/${areaId}/client-projects/${clientId}`)
          .then((res) => res.data);
      },
    },

    projects: {
      index: (
        divisionId: number,
        areaId: number,
        pageNumber?: number,
        searchQuery?: string,
      ) => {
        const params: { s?: string; p?: number } = {};

        if (searchQuery) {
          params.s = searchQuery;
        }
        if (pageNumber) {
          params.p = pageNumber;
        }
        
        return xhrService
          .get(`/api/areas/${areaId}/projects/${divisionId}`, { params })
          .then(handlePaginatedResponse.bind(null, 'project'));
      },
      create: (areaId, projectIds: number[], force?: boolean) => {
        return xhrService
          .post(`/api/areas/${areaId}/projects`, { projectIds, force })
          .then((res) => res.data);
      },
      remove: (areaId, projectId) => {
        return xhrService
          .delete(`/api/areas/${areaId}/projects`, {
            data: { projectId },
          })
          .then((res) => res.data);
      },
    },
  },
  documentTypeGroups: {
    index: (id, pageNumber?: number, searchQuery?: string) => {
      const params: { s?: string; p?: number } = {};

      if (searchQuery) {
        params.s = searchQuery;
      }

      if (pageNumber) {
        params.p = pageNumber;
      }

      return xhrService
        .get(`${baseUrl}/${id}/doc-type-groups`, { params })
        .then(handlePaginatedResponse.bind(null, 'doc_type_group'));
    },
    getAvailable: (divisionId:number) => {
      return xhrService
        .get(`${baseUrl}/${divisionId}/doc-type-groups/available`)
        .then((res) => res.data);
    },
    create: (id, data) => {
      const documentGroupIds = data.documentGroups.map(
        (group: { id: number }) => group.id,
      );

      return xhrService
        .post(`${baseUrl}/${id}/doc-type-groups/bulk`, { doc_type_group_ids:documentGroupIds })
        .then((res) => res.data);
    },
    delete: (id, data) => {
      return xhrService
        .delete(`${baseUrl}/${id}/doc-type-groups`, {
          data: { doc_type_group_ids: data },
        })
        .then((res) => res.data);
    },
  },
};

export default divisionApi;

function handlePaginatedResponse(key: string, res: { data: any }) {
  if (Array.isArray(res.data)) {
    return res.data.map((dc: any) => dc[key]);
  }

  if (res.data.pageData) {
    return {
      ...res.data,
      pageData: res.data.pageData.map((dc: any) => dc[key]),
    };
  }

  return res.data;
}
